import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["display", "loader"]
  static values = {
    autoRegenerate: { type: Boolean, default: false }
  }

  connect() {
    this.setupAutoSubmit()
    this.setupTurboListeners()
    this.setupDownloadHandler()
    this.setupSizeDisplay()
    this.initializeTrackedValues()
    
    // Auto-regenerate QR code if autoRegenerate is true
    if (this.autoRegenerateValue) {
      // Wait for form to be ready, then regenerate
      setTimeout(() => {
        this.submitForm()
      }, 300)
    }
  }

  setupSizeDisplay() {
    const form = this.element
    if (!form) return

    const sizeField = form.querySelector('[name="qr_config[size]"]')
    const sizeDisplay = document.getElementById('qr_size_value')
    
    if (sizeField && sizeDisplay) {
      sizeField.addEventListener('input', (e) => {
        sizeDisplay.textContent = `${e.target.value}px`
      })
    }
  }

  initializeTrackedValues() {
    const form = this.element
    if (!form) return

    const sizeField = form.querySelector('[name="qr_config[size]"]')
    const formatField = form.querySelector('[name="qr_config[format]"]')
    
    // Store as strings for consistent comparison
    this.initialSize = sizeField ? String(sizeField.value) : null
    this.initialFormat = formatField ? String(formatField.value) : null
  }

  hasSizeOrFormatChanged() {
    const form = this.element
    if (!form) return false

    const sizeField = form.querySelector('[name="qr_config[size]"]')
    const formatField = form.querySelector('[name="qr_config[format]"]')
    
    // Compare as strings for consistency
    const currentSize = sizeField ? String(sizeField.value) : null
    const currentFormat = formatField ? String(formatField.value) : null
    
    const sizeChanged = currentSize !== this.initialSize
    const formatChanged = currentFormat !== this.initialFormat
    
    return sizeChanged || formatChanged
  }

  setupDownloadHandler() {
    const form = this.element
    if (!form) return

    // Helper function to trigger download
    const triggerDownload = (url) => {
      try {
        // The URL from rails_blob_path already includes disposition=attachment
        // Just use it directly
        const link = document.createElement('a')
        link.href = url
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()
        
        // Clean up after a short delay
        setTimeout(() => {
          if (link.parentNode) {
            document.body.removeChild(link)
          }
        }, 100)
      } catch (error) {
        console.warn('Link click failed, using window.location:', error)
        // Fallback: navigate directly to the URL
        window.location.href = url
      }
    }

    // Handle download link clicks - always prevent default to control download behavior
    form.addEventListener('click', async (e) => {
      const downloadLink = e.target.closest('a[data-qr-download], a[href*="rails_blob"]')
      if (!downloadLink) return

      // Always prevent default to control the download
      e.preventDefault()
      e.stopPropagation()

      // Check if size or format has changed
      const hasChanged = this.hasSizeOrFormatChanged()
      
      if (hasChanged) {
        // Show loader
        this.showLoader()
        
        try {
          // Store the original href for comparison
          const originalHref = downloadLink.href
          
          // First regenerate the QR code - this will update both the display and download link via turbo stream
          await this.submitFormPromise()
          
          // Wait for turbo stream to update the download link AND verify the blob is available
          await new Promise((resolve) => {
            let attempts = 0
            const maxAttempts = 60 // 6 seconds max wait (QR generation can take time)
            
            const checkForUpdatedLink = async () => {
              const updatedLink = form.querySelector('a[data-qr-download], a[href*="rails_blob"]')
              if (!updatedLink) {
                if (attempts < maxAttempts) {
                  attempts++
                  setTimeout(checkForUpdatedLink, 100)
                } else {
                  console.warn('Download link not found after regeneration')
                  resolve()
                }
                return
              }
              
              // Check if URL changed (new blob was created)
              const urlChanged = updatedLink.href !== originalHref
              
              if (urlChanged) {
                // URL changed, wait a bit for blob to be ready, then verify
                await new Promise(r => setTimeout(r, 500))
                
                // Try to verify, but don't block on it
                const blobAvailable = await this.verifyBlobAvailable(updatedLink.href).catch(() => false)
                
                if (blobAvailable || attempts >= 10) {
                  // Blob is available or we've waited enough, proceed with download
                  // Update initial values to reflect the new state
                  this.initializeTrackedValues()
                  
                  // Trigger download with the updated link
                  triggerDownload(updatedLink.href)
                  resolve()
                } else {
                  // Blob not ready yet, keep checking
                  attempts++
                  setTimeout(checkForUpdatedLink, 200)
                }
              } else if (attempts < maxAttempts) {
                // URL hasn't changed yet, keep checking
                attempts++
                setTimeout(checkForUpdatedLink, 200)
              } else {
                // Timeout - URL didn't change, but try downloading anyway
                console.warn('URL did not change after regeneration, attempting download with current link')
                this.initializeTrackedValues()
                triggerDownload(updatedLink.href)
                resolve()
              }
            }
            
            // Start checking after turbo stream has had time to render
            setTimeout(checkForUpdatedLink, 500)
          })
        } catch (error) {
          console.warn('Failed to regenerate QR code before download:', error)
          // On error, try to download with current link
          const currentLink = form.querySelector('a[data-qr-download], a[href*="rails_blob"]')
          if (currentLink) {
            triggerDownload(currentLink.href)
          }
        } finally {
          this.hideLoader()
        }
      } else {
        // No changes, download directly
        triggerDownload(downloadLink.href)
      }
    }, true) // Use capture phase
  }

  setupTurboListeners() {
    // Hide loader when turbo stream updates the QR display
    document.addEventListener('turbo:before-stream-render', (event) => {
      const template = event.detail?.render
      if (template && typeof template === 'string' && template.includes('qr_code_display')) {
        // Hide loader when stream is about to update
        setTimeout(() => this.hideLoader(), 50)
      }
    })
  }

  setupAutoSubmit() {
    const form = this.element
    if (!form) return

    // Debounce auto-submit to avoid too many requests
    form.addEventListener('change', (e) => {
      // Skip if it's the download button
      if (e.target.closest('a[href*="rails_blob"]')) return
      
      // Skip size and format changes - they shouldn't trigger API calls
      if (e.target.name === 'qr_config[size]' || e.target.name === 'qr_config[format]') {
        return
      }
      
      clearTimeout(this.submitTimeout)
      this.submitTimeout = setTimeout(() => {
        this.submitForm()
      }, 500)
    })

    form.addEventListener('input', (e) => {
      // Skip size changes - they shouldn't trigger API calls
      if (e.target.name === 'qr_config[size]' || e.target.name === 'qr_config[format]') {
        return
      }
      
      clearTimeout(this.submitTimeout)
      this.submitTimeout = setTimeout(() => {
        this.submitForm()
      }, 500)
    })
  }

  submitForm() {
    const form = this.element
    if (!form) return

    // Show loader
    this.showLoader()

    // Submit form via turbo stream
    const formData = new FormData(form)
    const url = form.action
    
    if (!url) {
      console.warn('QR config form has no action URL')
      this.hideLoader()
      return
    }
    
    fetch(url, {
      method: form.method || 'POST',
      headers: {
        'Accept': 'text/vnd.turbo-stream.html',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
      },
      body: formData
    }).then(response => {
      if (response.ok) {
        return response.text()
      }
      throw new Error('Network response was not ok')
    }).then(html => {
      if (html) {
        Turbo.renderStreamMessage(html)
        // Loader will be hidden by turbo stream listener
      } else {
        this.hideLoader()
      }
    }).catch(error => {
      console.warn('QR config update failed:', error)
      this.hideLoader()
    })
  }

  submitFormPromise() {
    const form = this.element
    if (!form) return Promise.reject(new Error('Form not found'))

    // Submit form via turbo stream
    const formData = new FormData(form)
    const url = form.action
    
    if (!url) {
      return Promise.reject(new Error('QR config form has no action URL'))
    }
    
    return new Promise((resolve, reject) => {
      // Track if stream has completed
      let streamCompleted = false
      
      // Listen for turbo stream completion
      const handleStreamRender = () => {
        // Wait for DOM to update after stream render
        setTimeout(() => {
          if (!streamCompleted) {
            streamCompleted = true
            resolve()
          }
        }, 150)
      }
      
      // Listen for both before and after stream render
      document.addEventListener('turbo:before-stream-render', handleStreamRender, { once: true })
      document.addEventListener('turbo:after-stream-render', handleStreamRender, { once: true })
      
      // Fallback timeout
      const timeout = setTimeout(() => {
        if (!streamCompleted) {
          streamCompleted = true
          document.removeEventListener('turbo:before-stream-render', handleStreamRender)
          document.removeEventListener('turbo:after-stream-render', handleStreamRender)
          resolve()
        }
      }, 2000)
      
      fetch(url, {
        method: form.method || 'POST',
        headers: {
          'Accept': 'text/vnd.turbo-stream.html',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
        },
        body: formData
      }).then(response => {
        if (response.ok) {
          return response.text()
        }
        clearTimeout(timeout)
        streamCompleted = true
        document.removeEventListener('turbo:before-stream-render', handleStreamRender)
        document.removeEventListener('turbo:after-stream-render', handleStreamRender)
        throw new Error('Network response was not ok')
      }).then(html => {
        if (html) {
          Turbo.renderStreamMessage(html)
          // Promise will resolve when turbo stream completes
        } else {
          clearTimeout(timeout)
          streamCompleted = true
          document.removeEventListener('turbo:before-stream-render', handleStreamRender)
          document.removeEventListener('turbo:after-stream-render', handleStreamRender)
          resolve()
        }
      }).catch(error => {
        clearTimeout(timeout)
        streamCompleted = true
        document.removeEventListener('turbo:before-stream-render', handleStreamRender)
        document.removeEventListener('turbo:after-stream-render', handleStreamRender)
        reject(error)
      })
    })
  }

  showLoader() {
    // Show all loader targets
    if (this.hasLoaderTarget) {
      this.loaderTarget.classList.remove('hidden')
    } else {
      // Fallback: find loader in display element
      const display = this.getDisplayElement()
      if (display) {
        const loader = display.querySelector('[data-qr-config-target="loader"]')
        if (loader) {
          loader.classList.remove('hidden')
        }
      }
    }
  }

  hideLoader() {
    // Hide all loader targets
    if (this.hasLoaderTarget) {
      this.loaderTarget.classList.add('hidden')
    } else {
      // Fallback: find loader in display element
      const display = this.getDisplayElement()
      if (display) {
        const loader = display.querySelector('[data-qr-config-target="loader"]')
        if (loader) {
          loader.classList.add('hidden')
        }
      }
    }
  }

  getDisplayElement() {
    if (this.hasDisplayTarget) {
      return this.displayTarget
    }
    // Fallback: find by ID
    return document.getElementById('qr_code_display')
  }

  extractBlobId(url) {
    try {
      // Extract blob ID from Rails ActiveStorage URL
      // URL format: /rails/active_storage/blobs/:signed_id/:filename
      const match = url.match(/\/rails\/active_storage\/blobs\/([^\/]+)/)
      if (match && match[1]) {
        // The signed_id might be URL encoded, decode it
        return decodeURIComponent(match[1])
      }
      return null
    } catch (error) {
      console.warn('Failed to extract blob ID from URL:', error)
      return null
    }
  }

  async verifyBlobAvailable(url) {
    try {
      // Make a HEAD request to check if the blob is available
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ''
      const response = await fetch(url, {
        method: 'HEAD',
        headers: {
          'X-CSRF-Token': csrfToken
        },
        credentials: 'same-origin'
      })
      
      // If we get a 200 or 302, the blob is available
      return response.ok || response.status === 302
    } catch (error) {
      // If there's an error, assume blob is not ready yet
      return false
    }
  }

}

